import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import type { CustomFieldAssociation, UpdateAssociationDto } from '@/types/custom-fields';

interface UpdateAssociationVariables {
  id: string;
  dto: UpdateAssociationDto;
}

interface UseUpdateAssociationOptions {
  /**
   * When true, skip the auto success toast. Callers that widen an association
   * as one step of a larger save (the detail-page "Add custom field" dialog
   * extends a job-type scope, then writes the value) emit a single toast.
   */
  silent?: boolean;
}

export function useUpdateAssociation(options: UseUpdateAssociationOptions = {}) {
  const queryClient = useQueryClient();
  const silent = options.silent ?? false;
  return useApiMutation<CustomFieldAssociation, UpdateAssociationVariables>({
    mutationFn: ({ id, dto }) =>
      apiPatch<CustomFieldAssociation>(operationsClient, customFieldEndpoints.association(id), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
      if (!silent) toast.success('Association updated');
    },
  });
}
